import { zodResolver } from '@hookform/resolvers/zod';
import useFormError from 'api/hooks/useFormError';
import React, { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ErrorSection } from 'ui/Error';
import { PageModal } from 'ui/Modals';
import { PageModalActions, PageModalContent, PageModalTitle } from 'ui/Modals/PageModal';
import { z } from 'zod';
import { ButtonVariant } from 'ui/Button';
import { ParsedOrder } from 'pages/breeding/OrderList';
import ContactInputSelect from 'components/Contacts/ContactInputSelect';
import { Contact, Product, ProductTypeEnum } from 'openapi';
import { SelectInput } from 'ui/Inputs';
import { OptionItemInterface } from 'ui/Inputs/SelectInput';
import { useConfig } from 'context/ConfigContext';
import { downloadFile } from 'utilities/Files';
import { useAccount } from 'context/AccountContext';
import { Alert } from 'ui/Alert';
import { Severity } from 'utilities/severity';
import { PrintOrderShippingEnums } from 'types/Order';
import { wait } from 'utilities/Wait';

interface Props {
  loading: boolean;
  visible: boolean;
  onRequestCloseModal: () => void;
  onExported?: () => void;
  orders: ParsedOrder[];
  products: Product[] | undefined;
}

const schema = z.object({
  sws_contact_uid: z.string(),
  shipping_service_product_uid: z.string(),
});
type SchemaType = z.infer<typeof schema>;

export default function PrintShippingModal({ loading, visible, onRequestCloseModal, onExported, orders, products }: Props): JSX.Element {
  const [downloading, setDownloading] = useState<boolean>(false);
  const { session, formatDate } = useAccount();

  const { t } = useTranslation();
  const { config } = useConfig();

  /**
   * List the shipping products
   */
  const shippingProducts = useMemo((): OptionItemInterface[] => {
    if (!products) return [];

    return products
      .filter(product => product.product_type === ProductTypeEnum.SHIPPING && !!product.shipping_slip_number_start)
      .map<OptionItemInterface>(product => ({
        id: product.uid,
        name: `${product.shipping_provider_name} (${product.shipping_service_type})`,
      }));
  }, [products]);

  const semenWinStations = useMemo(() => {
    const semenOrderStations: Contact[] = [];
    for (const order of orders) {
      if (order.semenCollectionStation) {
        const station = semenOrderStations.find(station => station.uid === order.semenCollectionStation?.uid);
        if (!station) {
          semenOrderStations.push(order.semenCollectionStation);
        }
      }
    }

    return semenOrderStations;
  }, [orders]);

  const {
    handleSubmit,
    formState: { errors },
    reset,
    control,
    register,
    watch,
  } = useForm<SchemaType>({
    resolver: zodResolver(schema),
    reValidateMode: 'onChange',
  });

  const swsContactUidFormValue = watch('sws_contact_uid');
  const shippingServiceProductUidFormValue = watch('shipping_service_product_uid');

  const ordersUidToExport = useMemo(() => {
    return orders
      .filter(
        order =>
          order.semenCollectionStation?.uid === swsContactUidFormValue &&
          order.order.order_items?.some(item => item.product_uid === shippingServiceProductUidFormValue),
      )
      .map(order => order.order.uid);
  }, [orders, shippingServiceProductUidFormValue, swsContactUidFormValue]);

  const { fieldError, nonFieldErrors, setApiError } = useFormError(schema, errors);

  const onClosed = () => {
    reset();
    setApiError(undefined);
    setDownloading(false);
  };

  const downloadPdf = useCallback(
    async (
      ordersUidToExport: string[],
      type: PrintOrderShippingEnums,
      authToken: string,
    ): Promise<{ response: Response; filename: string }> => {
      const serverlessFunctionsUrl = `${config?.serverlessFunctionsUrl ?? ''}/api/topdf?type=${type}&order_uids=${ordersUidToExport.join(',')}`;

      await wait(1000);

      // try {
      let response = await fetch(serverlessFunctionsUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // We need to
          Authorization: `Bearer ${authToken}`,
        },
      });

      // Ok this is actually a funny part that needs some explanation. The pdf
      // generator (serverless function) might take a couple of seconds to
      // finish. In some cases with slow internet speeds this might trigger the
      // 10 second threshold. 10 seconds of serverless execution time is the
      // limit of Vercels free plan. It will throw a 504. But if we retrigger
      // the same download, it will be much faster due to Vercels caches. That's
      // why we will do 1 retry if we receive a 504.
      if (response.status === 504) {
        response = await fetch(serverlessFunctionsUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            // set the auth header to the token
            Authorization: `Bearer ${authToken}`,
          },
        });
      }

      let filename = `export_${formatDate(new Date())}.pdf`;
      if (type === PrintOrderShippingEnums.SLIP) {
        filename = `shipping_slip_${formatDate(new Date())}.pdf`;
      } else if (type === PrintOrderShippingEnums.MANIFEST) {
        filename = `shipping_manifest_${formatDate(new Date())}.pdf`;
      } else if (type === PrintOrderShippingEnums.ADDRESS_LABEL) {
        filename = `shipping_address_labels_${formatDate(new Date())}.pdf`;
      }

      return { response, filename };
    },
    [config?.serverlessFunctionsUrl, formatDate],
  );

  /**
   * Submit handler
   */
  const onSubmit = async (data: SchemaType) => {
    if (!session) return;

    const ordersUidToExport = orders
      .filter(
        order =>
          order.semenCollectionStation?.uid === data.sws_contact_uid &&
          order.order.order_items?.some(item => item.product_uid === data.shipping_service_product_uid),
      )
      .map(order => order.order.uid);

    // When there is no serverless function, we show some the debug urls in the console.
    // This is handy for local development.
    if (!config?.hasServerlessFunctions) {
      const urlManifest = `/p/shop/print-order-shipping-manifest/?order_uids=${ordersUidToExport.join(',')}`;
      const urlSlip = `/p/shop/print-order-shipping-slip/?order_uids=${ordersUidToExport.join(',')}`;
      const urlLabels = `/p/shop/print-order-shipping-address-labels/?order_uids=${ordersUidToExport.join(',')}`;

      alert('Serverless functions are not enabled');
      console.warn('Serverless functions are not enabled. Open the below urls manually.');
      console.warn(urlManifest);
      console.warn(urlSlip);
      console.warn(urlLabels);
      return;
    }

    setDownloading(true);

    try {
      const promise = await Promise.all([
        downloadPdf(ordersUidToExport, PrintOrderShippingEnums.SLIP, session.authToken),
        downloadPdf(ordersUidToExport, PrintOrderShippingEnums.ADDRESS_LABEL, session.authToken),
        downloadPdf(ordersUidToExport, PrintOrderShippingEnums.MANIFEST, session.authToken),
      ]);

      for (const result of promise) {
        if (result.response.status === 200) {
          await downloadFile(result.response, result.filename);
          await wait(500);
        } else {
          alert(`Failed to download ${result.filename}. Please check your internet connection and try again.`);
          console.error(`Failed to download ${result.filename} from serverless function`, await result.response.text());
        }
      }

      onExported?.();
    } catch (e) {
      alert(`Failed to download. Please check your internet connection and try again.`);
      console.error(`Failed to download from serverless function`, e);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <PageModal
        open={visible}
        onClosed={onClosed}
        parentElement='form'
        parentProps={{ id: 'exportOrders', noValidate: true, onSubmit: handleSubmit(onSubmit) }}
      >
        <PageModalTitle title={t('export-shipping-slip', 'Export shipping slip')} onClose={onRequestCloseModal} />
        <PageModalContent>
          {loading && (
            <Alert
              loading={true}
              severity={Severity.Info}
              message={t('export-shipping-slip-loading-more-orders', 'Hang tight, we are loading all order that match the selection')}
            />
          )}

          <ErrorSection errors={nonFieldErrors} />

          {swsContactUidFormValue && shippingServiceProductUidFormValue && ordersUidToExport.length === 0 && (
            <Alert
              className='mb-5'
              severity={Severity.Info}
              message={t(
                'export-shipping-slip-change-filter-no-result',
                'There are no shipping slips to generate for this shipping service. Please change your shipping service or semen collection station.',
              )}
            />
          )}

          <p className='mb-6'>
            {t(
              'export-shipping-slip-desc',
              'Below you can export your shipping slip per semen collection station and shipping service. First select your Semen collection station and shipping service to generate an export. The export will be downloaded as a PDF file.',
            )}
          </p>
          <div className='flex gap-2 justify-evenly'>
            <ContactInputSelect
              label={t('semen-collection-station', 'Semen collection station')}
              className='w-1/2'
              control={control}
              name='sws_contact_uid'
              error={fieldError('sws_contact_uid')}
              contacts={semenWinStations}
            />
            <SelectInput
              label={t('shipping-service', 'Shipping service')}
              className='w-1/2'
              nullable={true}
              nullableValue=''
              options={shippingProducts}
              error={fieldError('shipping_service_product_uid')}
              hint={t(
                'shipping-service-hint',
                'In order to add more shipping services, please enter a start number for your shipping slip in the settings of the shipping service.',
              )}
              {...register('shipping_service_product_uid')}
            />
          </div>
        </PageModalContent>
        <PageModalActions
          actions={[
            {
              loading: downloading,
              disabled: ordersUidToExport.length === 0 || loading,
              variant: ButtonVariant.Primary,
              text: t('export-[number]-orders', 'Export {{number}} orders', {
                number: ordersUidToExport.length,
              }),
              type: 'submit',
              formId: 'exportOrders',
            },
          ]}
        />
      </PageModal>
    </>
  );
}
